import { ChangeEvent, FC, FormEvent, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Server, Cloud, X } from "lucide-react";
import TextInput from "../../Components/TextInput";
import RadioButtonGroup from "../../Components/RadioGroupNew";
import styles from "./style.module.scss";

interface AddLLMModalProps {
  show: boolean;
  category: "query" | "embedding";
  onClose: () => void;
  onSave: (data: AddLLMFormData) => void;
}

export interface AddLLMFormData {
  provider: "self-hosted" | "aws-bedrock";
  modelName: string;
  endpointUrl: string;
  apiKey: string;
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
  temperature: number;
  maxTokens: number;
  dimension: number;
}

const providerOptions = [
  { id: 1, label: "Self - Hosted", icon: <Server size={16} /> },
  { id: 2, label: "AWS Bedrock", icon: <Cloud size={16} /> },
];

const AddLLMModal: FC<AddLLMModalProps> = ({ show, category, onClose, onSave }) => {
  const [provider, setProvider] = useState<1 | 2>(1);
  const [selfHostedModelName, setSelfHostedModelName] = useState("");
  const [selfHostedEndpointUrl, setSelfHostedEndpointUrl] = useState("");
  const [bedrockModelName, setBedrockModelName] = useState("");
  const [bedrockEndpointUrl, setBedrockEndpointUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [region, setRegion] = useState("");
  const [temperature, setTemperature] = useState(0.6);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [dimension, setDimension] = useState(1536);
  const [submitted, setSubmitted] = useState(false);

  const isSelfHosted = provider === 1;
  const isEmbedding = category === "embedding";

  const modelName = isSelfHosted ? selfHostedModelName : bedrockModelName;
  const setModelName = isSelfHosted ? setSelfHostedModelName : setBedrockModelName;
  const endpointUrl = isSelfHosted ? selfHostedEndpointUrl : bedrockEndpointUrl;
  const setEndpointUrl = isSelfHosted ? setSelfHostedEndpointUrl : setBedrockEndpointUrl;

  const resetForm = () => {
    setProvider(1);
    setSelfHostedModelName("");
    setSelfHostedEndpointUrl("");
    setBedrockModelName("");
    setBedrockEndpointUrl("");
    setApiKey("");
    setAccessKeyId("");
    setSecretAccessKey("");
    setRegion("");
    setTemperature(0.6);
    setMaxTokens(2000);
    setDimension(1536);
    setSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const onCancelClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitted(true);

    const isValid = isSelfHosted
      ? modelName.trim() && endpointUrl.trim() && apiKey.trim()
      : modelName.trim() && accessKeyId.trim() && secretAccessKey.trim() && region.trim();

    if (isValid) {
      onSave({
        provider: isSelfHosted ? "self-hosted" : "aws-bedrock",
        modelName,
        endpointUrl,
        apiKey: isSelfHosted ? apiKey : "",
        accessKeyId: isSelfHosted ? "" : accessKeyId,
        secretAccessKey: isSelfHosted ? "" : secretAccessKey,
        region: isSelfHosted ? "" : region,
        temperature,
        maxTokens,
        dimension,
      });
      resetForm();
    }
  };

  const handleTemperatureSlider = (e: ChangeEvent<HTMLInputElement>) => {
    setTemperature(parseFloat(e.target.value));
  };

  const handleTemperatureInput = (e: ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val) && val >= 0 && val <= 1) {
      setTemperature(val);
    }
  };

  const handleMaxTokensChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 0) {
      setMaxTokens(val);
    }
  };

  // Show error only after submit attempt AND field is still empty
  const showError = (value: string) => submitted && !value.trim();

  const sliderPercent = temperature * 100;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      className={styles.addLLMModal}
    >
      <Form noValidate onSubmit={handleSubmit}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            {category === "query" ? "Query Model" : "Embedding Model"}
          </div>
          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Provider Toggle */}
          <div className={styles.providerSection}>
            <div className={styles.providerLabel}>Select provider</div>
            <RadioButtonGroup
              options={providerOptions}
              value={provider}
              name="llm-provider"
              onChange={(val) => {
                setProvider(val as 1 | 2);
                setSubmitted(false);
              }}
            />
          </div>

          {/* Model Info Section */}
          <div className={styles.formSection}>
            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Model Name</div>
                <TextInput
                  name="modelName"
                  id="addllm-modelName"
                  value={modelName}
                  placeholder={
                    isSelfHosted ? "e.g., llama-3-70b" : "e.g., anthropic.claude-v3"
                  }
                  isInvalid={showError(modelName)}
                  errrorMessage={showError(modelName) ? "Model name is required" : ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setModelName(e.target.value)
                  }
                />
              </div>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  Endpoint URL{!isSelfHosted && " (Optional)"}
                </div>
                <TextInput
                  name="endpointUrl"
                  id="addllm-endpointUrl"
                  value={endpointUrl}
                  placeholder="e.g: https://api.example.com/v1"
                  isInvalid={isSelfHosted && showError(endpointUrl)}
                  errrorMessage={isSelfHosted && showError(endpointUrl) ? "Endpoint URL is required" : ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEndpointUrl(e.target.value)
                  }
                />
              </div>
            </div>

            {/* Self-Hosted: API Key + Dimension */}
            {isSelfHosted && (
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <div className={styles.fieldLabel}>API Key</div>
                  <TextInput
                    name="apiKey"
                    id="addllm-apiKey"
                    type="password"
                    value={apiKey}
                    placeholder="Enter your API key"
                    isInvalid={showError(apiKey)}
                    errrorMessage={showError(apiKey) ? "API key is required" : ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setApiKey(e.target.value)
                    }
                  />
                </div>
                {isEmbedding ? (
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>Dimension</div>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      value={dimension}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0) setDimension(val);
                      }}
                      className={styles.numberInput}
                    />
                  </div>
                ) : (
                  <div className={styles.fieldGroup} />
                )}
              </div>
            )}
          </div>

          {/* AWS Bedrock: Credentials Section */}
          {!isSelfHosted && (
            <div className={styles.formSection}>
              <div className={styles.sectionTitle}>AWS Credentials</div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <div className={styles.fieldLabel}>Access Key ID</div>
                  <TextInput
                    name="accessKeyId"
                    id="addllm-accessKeyId"
                    type="password"
                    value={accessKeyId}
                    placeholder="Enter access key ID"
                    isInvalid={showError(accessKeyId)}
                    errrorMessage={showError(accessKeyId) ? "Access Key ID is required" : ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setAccessKeyId(e.target.value)
                    }
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <div className={styles.fieldLabel}>Secret Access Key</div>
                  <TextInput
                    name="secretAccessKey"
                    id="addllm-secretAccessKey"
                    type="password"
                    value={secretAccessKey}
                    placeholder="Enter secret key"
                    isInvalid={showError(secretAccessKey)}
                    errrorMessage={showError(secretAccessKey) ? "Secret Access Key is required" : ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSecretAccessKey(e.target.value)
                    }
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <div className={styles.fieldLabel}>Region</div>
                  <TextInput
                    name="region"
                    id="addllm-region"
                    value={region}
                    placeholder="e.g: us-west-2a"
                    isInvalid={showError(region)}
                    errrorMessage={showError(region) ? "Region is required" : ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setRegion(e.target.value)
                    }
                  />
                </div>
                {isEmbedding ? (
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>Dimension</div>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      value={dimension}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0) setDimension(val);
                      }}
                      className={styles.numberInput}
                    />
                  </div>
                ) : (
                  <div className={styles.fieldGroup} />
                )}
              </div>
            </div>
          )}

          {/* Generation Controls */}
          <div className={styles.formSection}>
            <div className={styles.sectionTitle}>Generation Controls</div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Temperature</div>
                <div className={styles.sliderRow}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={temperature}
                    onChange={handleTemperatureSlider}
                    className={styles.temperatureSlider}
                    style={{
                      background: `linear-gradient(to right, #102250 0%, #102250 ${sliderPercent}%, #e0e0e0 ${sliderPercent}%, #e0e0e0 100%)`,
                    }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={temperature}
                    onChange={handleTemperatureInput}
                    className={styles.temperatureInput}
                  />
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Maximum Tokens</div>
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={maxTokens}
                  onChange={handleMaxTokensChange}
                  className={styles.numberInput}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button type="button" className={styles.cancelBtn} onClick={onCancelClick}>
            Cancel
          </button>
          <button type="submit" className={styles.saveBtn}>
            Save
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddLLMModal;
